import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal } from "@/components/ui/customs/modal";
import { RibInfoDto } from "@/types/api/api-types";
import { Copy } from "lucide-react";

type RibModalProps = {
  closeModal: () => void;
  isOpen?: boolean;
  rib: RibInfoDto | null;
};

export const RibModal = ({ closeModal, isOpen, rib }: RibModalProps) => {
  const { t } = useTranslation("boClients");
  return (
    <Modal
      open={!!isOpen}
      onClose={closeModal}
      title={t("invoicesRibModal.title")}
      size="md"
      dismissible
      contentClassName="min-w-0 overflow-x-hidden xl:max-w-[600px]"
    >
      {rib ? (
        <div className="flex w-full min-w-0 flex-col gap-4">
          <Display title={t("invoicesRibModal.beneficiaryLabel")} text={rib.beneficiary} />
          <Display title={t("invoicesRibModal.bankLabel")} text={rib.bankName} />
          <Display
            title="IBAN"
            text={rib.iban
              .replace(/\s/g, "")
              .replace(/(.{4})/g, "$1 ")
              .trim()}
          />
          <Display title="BIC" text={rib.bic} />
        </div>
      ) : (
        <p className="text-sm font-medium text-gray-600">{t("invoicesRibModal.empty")}</p>
      )}
    </Modal>
  );
};

type DisplayProps = {
  title: string;
  text: string;
};

const Display = ({ title, text }: DisplayProps) => {
  const { t } = useTranslation("boClients");
  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    toast.success(t("invoicesRibModal.copySuccess"));
  };

  return (
    <div className="flex min-w-0 flex-1 flex-row gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className="hidden w-full min-w-0 flex-row items-start justify-between gap-2 rounded-lg border bg-gray-50 px-2 py-2 md:flex">
          <p className="min-w-0 flex-1 text-xs font-medium break-all text-gray-600 md:text-base">
            {text}
          </p>
          <button
            type="button"
            className="text-brand600 shrink-0 rounded-full p-1 hover:bg-gray-200 focus:outline-hidden"
            onClick={copyToClipboard}
          >
            <Copy width={20} height={20} color="#06F" />
          </button>
        </div>
        <button
          type="button"
          className="flex w-full min-w-0 flex-row items-start justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 md:hidden"
          onClick={copyToClipboard}
        >
          <p className="min-w-0 flex-1 text-left text-xs font-medium break-all text-gray-600 md:text-base">
            {text}
          </p>
        </button>
      </div>
    </div>
  );
};
